import { Button, Group } from '@mantine/core';
import { modals } from '@mantine/modals';
import { IconPlus } from '@tabler/icons-react';
import { useGetTasks, useUpdateTasksStatus } from 'api/index';
import type { GetTasksParams, Task, TaskActionsResponse, UpdateTasksStatusBody } from 'api/models';
import classNames from 'classnames/bind';
import { useProfile } from 'init/use-enforce-profile';
import { i18nKeys, useTranslation } from 'locales';
import { pick } from 'lodash-es';
import { TaskForm } from 'modules/Tasks/TaskForm';
import qs from 'query-string';
import { useEffect, useMemo, useState } from 'react';
import { useHistory, useLocation } from 'react-router';
import CustomTableViews from 'shared/components/CustomTable/CustomTableViews';
import { flattenJsonApiRow } from 'shared/components/CustomTable/hooks/loadViewData';
import ListResModal from 'shared/components/DeleteModal';
import { queryParamParser } from 'shared/utils/view';
import { DialogShowId, DialogShowSize, dialogHide, showDialog } from 'store/view/view.actions';
import type { TasksListFiltersFields } from '../Task.constants';
import { TaskActions } from '../TaskActions/TaskActions';
import { ExportTasksCsv } from './ExportTasksCsv';
import styleIdentifiers from './TasksList.module.scss';
import { TaskListItemActions } from './TasksListItemActions';

const styles = classNames.bind(styleIdentifiers);

export const CustomTasksList = () => {
  const { t } = useTranslation();
  const location = useLocation();
  const history = useHistory();
  const profile = useProfile();

  const [selectedTasks, setSelectedTasks] = useState<Task[]>([]);
  const [areAllItemsAcrossPagesSelected, setAreAllItemsAcrossPagesSelected] = useState(false);

  const [params, setParams] = useState<GetTasksParams>({
    view_id: location.hash.slice(1),
    page_limit: profile.preferences.itemsPerPage ?? 20,
    page: 1,
  });

  const { data: tasksListResponse, isLoading, refetch } = useGetTasks(params);
  const { mutate: updateTasksStatus } = useUpdateTasksStatus();

  // The Tasks list endpoint returns JSON:API-shaped rows ({ id, type, attributes });
  // flatten them upstream (the same way loadViewData does for the generic views) so the
  // table layer receives flat rows like every other view.
  const taskRows = useMemo(
    () =>
      (tasksListResponse?.data ?? []).map(row =>
        flattenJsonApiRow(row as unknown as Record<string, unknown>),
      ),
    [tasksListResponse],
  );

  useEffect(() => {
    setParams(() => ({
      view_id: location.hash.slice(1),
      page_limit: profile.preferences.itemsPerPage ?? 20,
      page: 1,
      ...pick<GetTasksParams>(Object.fromEntries(new URLSearchParams(location.search)), [
        'page',
        'page_limit',
        'sort_by',
        'sort_order',
        'title',
        'title_handler',
        'description',
        'description_handler',
        'status',
        'status_handler',
        'user_id',
        'user_id_handler',
        'additional_user_ids',
        'additional_user_ids_handler',
        'debtors__full_name',
        'debtors__full_name_handler',
        'debtors__paused',
        'debtors__disputed',
        'debtors__paused_reason_id',
        'debtors__disputed_reason_id',
        'invoice_ids',
        'invoice_ids_handler',
        'mandatory',
        'mandatory_handler',
        'task_type_id',
        'task_type_id_handler',
        'recovery_plan_id',
        'recovery_plan_id_handler',
        'days_postponed',
        'min_days_postponed',
        'max_days_postponed',
        'days_postponed_handler',
        'created_at_start',
        'created_at_handler',
        'created_at_end',
        'due_date_start',
        'due_date_handler',
        'due_date_end',
        'updated_at_start',
        'updated_at_handler',
        'updated_at_end',
        'skipped_at_start',
        'skipped_at_handler',
        'skipped_at_end',
        'completed_at_start',
        'completed_at_handler',
        'completed_at_end',
        'quick_search',
        // This is custom filter. For the future deveopment
        // we should dynamically include all custom filters in allowed params list
        'debtor__recovr',
        'debtor__associe',
      ]),
    }));
  }, [location, setParams]);

  const addTask = () => {
    modals.open({
      title: t(i18nKeys.FORM.TASK.NEW),
      size: 'xl',
      children: <TaskForm refetchTasks={refetch} />,
    });
  };

  const batchActions = (data: UpdateTasksStatusBody) =>
    updateTasksStatus(
      {
        data: {
          ...data,
          filter_params: queryParamParser(qs.parse(location.search)),
          view_id: areAllItemsAcrossPagesSelected ? location.hash.slice(1) : undefined,
        },
      },
      {
        onSuccess: (response: TaskActionsResponse) => {
          if (Array.isArray(response)) {
            showDialog({
              id: DialogShowId.CUSTOM,
              size: DialogShowSize.MEDIUM,
              title: t(i18nKeys.RESULT),
              children: <ListResModal data={response} />,
            });
          } else {
            dialogHide(DialogShowId.CUSTOM);
          }
          refetch();
        },
      },
    );

  const handleFilters = (data: TasksListFiltersFields) => {
    const filters = {
      ...pick(params, ['page_limit', 'page']),
      ...data,
    };

    setParams(filters);
    return filters;
  };

  const taskIds = areAllItemsAcrossPagesSelected ? undefined : selectedTasks.map(task => task.id);

  const goToDetails = (task: any, e: React.MouseEvent<HTMLTableRowElement>) => {
    if (e.metaKey || e.ctrlKey || e.button === 1) {
      window.open(`/tasks/${task.id}`, '_blank');
      return;
    }
    history.push(`/tasks/${task.id}`);
  };

  const actionsCol = (item: any) => (
    <TaskListItemActions item={item} noCheckbox refetch={refetch} />
  );

  const handleClickExport = () => {
    showDialog({
      id: DialogShowId.CUSTOM,
      size: DialogShowSize.MEDIUM,
      title: t(i18nKeys.EXPORT_CSV),
      children: <ExportTasksCsv />,
    });
  };

  return (
    <CustomTableViews
      className={styles('listing')}
      title={t(i18nKeys.TASK.TITLE)}
      callbackAction={refetch}
      onClickRow={goToDetails}
      actions={
        <Group h={36} gap="sm">
          {areAllItemsAcrossPagesSelected || !!selectedTasks.length ? (
            <TaskActions taskIds={taskIds} onClick={batchActions} />
          ) : (
            <>
              <Button variant="default" onClick={handleClickExport}>
                {t(i18nKeys.EXPORT_CSV)}
              </Button>
              <Button onClick={addTask} leftSection={<IconPlus size={16} stroke={1.5} />}>
                {t(i18nKeys.NEW_TASK)}
              </Button>
            </>
          )}
        </Group>
      }
      isLoading={isLoading}
      pagination={tasksListResponse?.metadata.pagination}
      handleFilters={handleFilters}
      items={taskRows}
      onChangeSelectionCallback={vals => setSelectedTasks(vals as Task[])}
      onChangeSelectItemsAcrossAllPagesCallback={setAreAllItemsAcrossPagesSelected}
      tableName="tasks"
      actionsCol={actionsCol}
    />
  );
};
